import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import QuickActionsSidebar from "../DistributorDashboard/QuickActionsSidebar";
import { getAdminActions } from "../../../config/actions";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { API_URL } from "../../../config";
import "../DistributorsAdd/DistributorsAdd.css";
import "../DistributorDashboard/ManageCustomers.css";
import "./CapnoUsersAdmin.css";

const USER_TYPE_LABELS = {
  1: "Admin",
  2: "Trainer",
  3: "Client",
  4: "Group",
  5: "Go Personal",
  6: "Go Basic",
  7: "Go Professional",
  9: "CapnoGO",
};

const naIfEmpty = (value) => {
  if (value === null || value === undefined) return "N/A";
  const str = String(value).trim();
  return str === "" || str === "0" ? "N/A" : str;
};

// Same style as the API's email masking: first two characters, rest starred.
const maskName = (value) => {
  const str = naIfEmpty(value);
  if (str === "N/A") return str;
  return `${str.slice(0, 2)}${"*".repeat(Math.max(str.length - 2, 1))}`;
};

// length_ms is computed by the API from the session's signal CSV;
// record_length is the raw value stored with the record, used as a fallback.
const formatSessionLength = (session) => {
  if (!session) return "N/A";
  if (session.length_ms === null || session.length_ms === undefined) {
    return naIfEmpty(session.record_length);
  }
  const totalSeconds = Math.round(session.length_ms / 1000);
  const pad = (n) => String(n).padStart(2, "0");
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  return `${pad(hours)}:${pad(minutes)}:${pad(totalSeconds % 60)}`;
};

const PASSWORD_CHARS =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

const generatePassword = (length = 10) => {
  const values = new Uint32Array(length);
  window.crypto.getRandomValues(values);
  return Array.from(values, (v) => PASSWORD_CHARS[v % PASSWORD_CHARS.length]).join("");
};

const CapnoUsersAdmin = () => {
  const navigate = useNavigate();
  const userType = localStorage.getItem("user_type");
  const userId = localStorage.getItem("user_id");

  if (userType !== "7") {
    navigate("/");
    return null;
  }

  const adminActions = getAdminActions(userId);
  const [expandedMenu, setExpandedMenu] = useState(null);

  const [searchEmail, setSearchEmail] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");

  const [selectedUser, setSelectedUser] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState("");
  const [resetDone, setResetDone] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    const trimmed = searchEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setSearchError("Enter a valid CapnoGO user email.");
      return;
    }

    setSearching(true);
    setSearchError("");
    try {
      const token = localStorage.getItem("accessToken");
      const res = await fetch(
        `${API_URL}/admin/capno-users/search?email=${encodeURIComponent(trimmed)}`,
        { headers: { "x-access-token": token } }
      );
      const data = await res.json();
      if (data.user) {
        setSelectedUser({ ...data.user, match_count: data.match_count });
        setViewModalOpen(true);
      } else {
        setSearchError(`No CapnoGO user found with email ${trimmed}.`);
      }
    } catch (error) {
      console.error("Error searching CapnoGO user:", error);
      setSearchError("Could not search right now. Please try again.");
    } finally {
      setSearching(false);
    }
  };

  const toggleViewModal = () => {
    setViewModalOpen(!viewModalOpen);
    if (viewModalOpen) {
      setSelectedUser(null);
    }
  };

  const openResetModal = () => {
    setConfirmOpen(false);
    setNewPassword(generatePassword());
    setResetError("");
    setResetDone(false);
    setResetOpen(true);
  };

  const closeResetModal = () => {
    setResetOpen(false);
    setNewPassword("");
    setResetError("");
    setResetDone(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      setResetError("Password must be at least 6 characters.");
      return;
    }

    setResetting(true);
    setResetError("");
    try {
      const token = localStorage.getItem("accessToken");
      const res = await fetch(
        `${API_URL}/admin/capno-users/${selectedUser.id}/reset-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-access-token": token,
          },
          body: JSON.stringify({ newPassword }),
        }
      );
      const data = await res.json();
      if (data.status) {
        setResetDone(true);
      } else {
        setResetError(data.message || "Could not reset the password.");
      }
    } catch (error) {
      console.error("Error resetting CapnoGO user password:", error);
      setResetError("Could not reset the password. Please try again.");
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="distributors-add-container capno-users-page">
      <div className="distributors-add-dashboard">
        <QuickActionsSidebar
          actions={adminActions}
          expandedMenu={expandedMenu}
          setExpandedMenu={setExpandedMenu}
        />
        <div className="distributors-add-content">
          <div className="add-btn-wrp add-btn-wrp2">
            <div className="head-listing">
              <h3>CapnoGO Users</h3>
            </div>
            <div className="add-btn capno-user-header-actions">
              <button onClick={() => navigate("/admin/capnogo-tickets")}>
                Tickets
              </button>
            </div>
          </div>

          <form
            className="filters-section capno-user-search"
            autoComplete="off"
            onSubmit={handleSearch}
          >
            <div className="filter-group">
              <label htmlFor="capno-user-email">Search by Email:</label>
              <input
                id="capno-user-email"
                type="text"
                placeholder="Enter user email"
                autoComplete="off"
                value={searchEmail}
                onChange={(e) => {
                  setSearchEmail(e.target.value);
                  setSearchError("");
                }}
              />
            </div>
            <button
              type="submit"
              className="capno-user-search-btn"
              disabled={searching || !searchEmail.trim()}
            >
              {searching ? "Searching..." : "Search"}
            </button>
            {searchError && (
              <div className="capno-user-search-error">{searchError}</div>
            )}
          </form>
        </div>
      </div>

      {/* View User Modal */}
      <Modal isOpen={viewModalOpen} toggle={toggleViewModal} centered size="lg">
        <ModalHeader toggle={toggleViewModal} style={{color: "#fff"}}>
          <i className="fas fa-user"></i> User Details
        </ModalHeader>
        <ModalBody>
          {selectedUser && (
            <div className="capno-user-details">
              {/* {selectedUser.match_count > 1 && (
                <div className="capno-user-match-note">
                  {selectedUser.match_count} users share this email. Showing
                  the oldest account (user ID {selectedUser.id}).
                </div>
              )} */}
              <div className="detail-row">
                <span className="label">User ID:</span>
                <span className="value">{selectedUser.id}</span>
              </div>
              <div className="detail-row">
                <span className="label">User Type:</span>
                <span className="value">
                  {USER_TYPE_LABELS[String(selectedUser.user_type)] ||
                    `Type ${selectedUser.user_type}`}
                </span>
              </div>
              <div className="detail-row">
                <span className="label">First Name:</span>
                <span className="value">{maskName(selectedUser.firstname)}</span>
              </div>
              <div className="detail-row">
                <span className="label">Last Name:</span>
                <span className="value">{maskName(selectedUser.lastname)}</span>
              </div>
              <div className="detail-row">
                <span className="label">Email:</span>
                <span className="value">{naIfEmpty(selectedUser.email)}</span>
              </div>
              {/* <div className="detail-row password-field">
                <span className="label">Password:</span>
                <span className="password-value">••••••••••</span>
              </div> */}
                <div className="detail-row">
                <span className="label">Status:</span>
                <span className="value">
                  {Number(selectedUser.status) === 1 ? "Active" : "Inactive"}
                </span>
              </div>
              <div className="detail-row">
                <span className="label">Last Session :</span>
                <span className="value">
                  {naIfEmpty(selectedUser.last_session?.name)}
                </span>
              </div>
              <div className="detail-row">
                <span className="label">Session Length:</span>
                <span className="value">
                  {formatSessionLength(selectedUser.last_session)}
                </span>
              </div>

              {/* <div className="detail-row">
                <span className="label">Telephone:</span>
                <span className="value">{naIfEmpty(selectedUser.telephone)}</span>
              </div>
              <div className="detail-row">
                <span className="label">Gender:</span>
                <span className="value">{naIfEmpty(selectedUser.gender)}</span>
              </div>
              <div className="detail-row">
                <span className="label">Age:</span>
                <span className="value">{naIfEmpty(selectedUser.age)}</span>
              </div>
              <div className="detail-row">
                <span className="label">Profession:</span>
                <span className="value">{naIfEmpty(selectedUser.profession)}</span>
              </div>
              <div className="detail-row">
                <span className="label">Address:</span>
                <span className="value">
                  {naIfEmpty(
                    [selectedUser.address, selectedUser.address2]
                      .filter(Boolean)
                      .join(", ")
                  )}
                </span>
              </div>
              <div className="detail-row">
                <span className="label">City:</span>
                <span className="value">{naIfEmpty(selectedUser.city)}</span>
              </div>
              <div className="detail-row">
                <span className="label">State:</span>
                <span className="value">{naIfEmpty(selectedUser.state)}</span>
              </div>
              <div className="detail-row">
                <span className="label">Country:</span>
                <span className="value">{naIfEmpty(selectedUser.country)}</span>
              </div>
              <div className="detail-row">
                <span className="label">Zipcode:</span>
                <span className="value">{naIfEmpty(selectedUser.zipcode)}</span>
              </div> */}
            
            </div>
          )}
        </ModalBody>
        <ModalFooter className="capno-user-modal-footer">
          <button
            className="capno-user-modal-btn capno-user-modal-btn-primary"
            onClick={() => setConfirmOpen(true)}
          >
            Reset Password
          </button>
          <button className="capno-user-modal-btn" onClick={toggleViewModal}>
            Close
          </button>
        </ModalFooter>
      </Modal>

      {/* Reset Password Confirmation Modal */}
      <Modal isOpen={confirmOpen} toggle={() => setConfirmOpen(false)} centered>
        <ModalHeader toggle={() => setConfirmOpen(false)} style={{color: "#fff"}}>
          <i className="fas fa-key"></i> Reset Password
        </ModalHeader>
        <ModalBody>
          Are you sure you want to reset the password for user ID{" "}
          <strong>{selectedUser?.id}</strong>?
        </ModalBody>
        <ModalFooter className="capno-user-modal-footer">
          <button
            className="capno-user-modal-btn capno-user-modal-btn-primary"
            onClick={openResetModal}
          >
            Yes, Reset
          </button>
          <button className="capno-user-modal-btn" onClick={() => setConfirmOpen(false)}>
            Cancel
          </button>
        </ModalFooter>
      </Modal>

      {/* Reset Password Modal */}
      <Modal isOpen={resetOpen} toggle={closeResetModal} centered>
        <ModalHeader toggle={closeResetModal} style={{color: "#fff"}}>
          <i className="fas fa-key"></i> Reset Password
        </ModalHeader>
        <form autoComplete="off" onSubmit={handleResetPassword}>
          <ModalBody>
            <div className="capno-user-reset-form">
              <label htmlFor="capno-user-new-password">
                New password for user ID {selectedUser?.id}
              </label>
              <div className="capno-user-reset-input">
                <input
                  id="capno-user-new-password"
                  type="text"
                  autoComplete="off"
                  value={newPassword}
                  readOnly={resetDone}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    setResetError("");
                  }}
                />
                {!resetDone && (
                  <button
                    type="button"
                    className="capno-user-modal-btn"
                    onClick={() => {
                      setNewPassword(generatePassword());
                      setResetError("");
                    }}
                    disabled={resetting}
                  >
                    Generate
                  </button>
                )}
              </div>
              {!resetDone && (
                <div className="capno-user-reset-hint">
                  Auto-generated. Keep it or type your own (minimum 6
                  characters).
                </div>
              )}
              {resetError && (
                <div className="capno-user-search-error">{resetError}</div>
              )}
              {resetDone && (
                <div className="capno-user-reset-success">
                  Password reset successfully. Share this password with the
                  user.
                </div>
              )}
            </div>
          </ModalBody>
          <ModalFooter className="capno-user-modal-footer">
            {!resetDone && (
              <button
                type="submit"
                className="capno-user-modal-btn capno-user-modal-btn-primary"
                disabled={resetting || !newPassword}
              >
                {resetting ? "Saving..." : "Save Password"}
              </button>
            )}
            <button
              type="button"
              className="capno-user-modal-btn"
              onClick={closeResetModal}
              disabled={resetting}
            >
              {resetDone ? "Close" : "Cancel"}
            </button>
          </ModalFooter>
        </form>
      </Modal>
    </div>
  );
};

export default CapnoUsersAdmin;
